import _ from 'lodash';
import React from 'react';
import classNames from 'classnames';
import MuiSelect, { SelectProps as BaseSelectProps } from '@material-ui/core/Select';
import MuiMenuItem from '@material-ui/core/MenuItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import { makeStyles } from '../styles';
import { Colors } from 'react-scale/components/legacy-css/global_css';

export interface SelectProps extends BaseSelectProps {
  label?: React.ReactNode;
  placeholder?: string;
  name?: string;
  value?: unknown;
  onChange?: React.ChangeEventHandler<{ value: unknown }>;
  onOpen?: (event: any) => void;
  onClose?: (event: any) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  style?: React.CSSProperties;
  menuPropsStyle?: React.CSSProperties;
  menuPaperStyle?: string;
  columns?: number;
  classes?: {
    root?: string;
    label?: string;
    icon?: string;
    control?: string;
  };
  multiple?: boolean;
  disablePortal?: boolean;
  anchorEl?: HTMLElement;
  renderValue?: (value: unknown) => React.ReactNode;
  disableBackground?: boolean;
  defaultOpen?: boolean;
  menuProps?: object;
  fullWidth?: boolean;
}

export const Select: React.FC<SelectProps> = props => {
  const classes = useSelectStyles(props);

  const renderPlaceholder = (): React.ReactNode => {
    return (
      <label
        className={classNames(classes.label, props.classes?.label, {
          [classes.disabled]: props.disabled,
        })}
      >
        <em style={{ opacity: 0.5 }}>{props.placeholder || props.label}</em>
      </label>
    );
  };

  // Keep the default MuiSelectInput logic with computeDisplay unless value is blank.
  const renderValue =
    props.renderValue ||
    (typeof props.value === 'undefined' || (_.isArray(props.value) && props.value?.length === 0)
      ? renderPlaceholder
      : undefined);
  return (
    <div className={classNames(classes.control, props.classes?.control)}>
      <MuiSelect
        disabled={props.disabled}
        MenuProps={
          props.anchorEl
            ? {
                anchorEl: props.anchorEl,
                classes: { list: classes.menu, paper: props.menuPaperStyle },
                ...(props.menuPropsStyle && { style: props.menuPropsStyle }),
                disablePortal: props.disablePortal,
                ...props.menuProps,
              }
            : {
                classes: { list: classes.menu, paper: props.menuPaperStyle },
                ...(props.menuPropsStyle && { style: props.menuPropsStyle }),
                disablePortal: props.disablePortal,
                ...props.menuProps,
              }
        }
        classes={{
          root: classNames(classes.root, props.classes?.root, {
            [classes.empty]: typeof props.value === 'undefined' && !props.disableBackground,
            [classes.notEmpty]: !(typeof props.value === 'undefined') && !props.disableBackground,
          }),
          icon: classNames(classes.icon, props.classes?.icon, {
            [classes.iconDefault]: !props.disabled,
            [classes.iconDisabled]: props.disabled,
          }),
        }}
        style={{ width: props.fullWidth ? '100%' : undefined, ...props.style }}
        value={props.value}
        name={props.name}
        renderValue={renderValue}
        onChange={props.onChange}
        onOpen={props.onOpen}
        onClose={props.onClose}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
        disableUnderline
        displayEmpty
        multiple={props.multiple}
        open={props.defaultOpen}
        variant={props.variant}
        native={props.native}
        endAdornment={props.endAdornment}
      >
        {props.children}
      </MuiSelect>
    </div>
  );
};

const useSelectStyles = makeStyles<SelectProps>(() => ({
  control: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    color: '#000',
    width: props => (props.fullWidth ? '100%' : undefined),
  },
  label: {
    display: 'inline-block',
    fontSize: '14px',
    lineHeight: '20px',
    pointerEvents: 'none',
  },
  root: {
    height: 32,
    boxSizing: 'border-box',
    borderRadius: 8,
    paddingLeft: 16,
    paddingRight: '40px !important',
    '&:focus': {
      borderRadius: 8,
    },
    fontSize: '14px',
    lineHeight: '20px',
  },
  empty: {
    background: Colors.RemoGray05,
    '&:focus': {
      background: Colors.RemoGray05,
    },
  },
  notEmpty: {
    background: Colors.RemoGray10,
    '&:focus': {
      background: Colors.RemoGray10,
    },
  },
  icon: {
    top: 8,
    right: 16,
    width: 16,
    height: 16,
  },
  iconDefault: {
    color: '#000',
  },
  iconDisabled: {
    color: Colors.RemoGray50,
  },
  menu: props => ({
    paddingTop: 6,
    paddingBottom: 6,
    display: 'grid',
    gridTemplateColumns: `repeat(${props.columns ?? 1}, 1fr)`,
  }),
  disabled: {
    color: Colors.RemoGray50,
  },
}));

interface OptionProps {
  value?: any;
  disabled?: boolean;
  className?: string;
  disableRipple?: boolean;
  children?: React.ReactNode;
}

export const Option: React.FC<OptionProps> = React.forwardRef<HTMLLIElement, OptionProps>(
  (props, ref) => {
    const classes = useOptionStyles(props);
    // Very important to pass all props. Select adds additional props.
    return (
      <MuiMenuItem {...props} classes={{ root: classes.root }} ref={ref}>
        {props.children}
      </MuiMenuItem>
    );
  },
);
Option.displayName = 'Option';

type OptgroupProps = React.ComponentProps<typeof ListSubheader>;

export const Optgroup: React.FC<OptgroupProps> = props => {
  const classes = useOptionStyles(props);
  return (
    <ListSubheader {...props} classes={{ root: classes.root }}>
      {props.children}
    </ListSubheader>
  );
};

Optgroup.displayName = 'Optgroup';

const useOptionStyles = makeStyles({
  root: {
    fontSize: '14px',
    lineHeight: '20px',
  },
});
